/**
 * Guests step: scanning the ID and typing the guest details are one flow or the other, never both.
 * With nothing scanned or typed both options are offered; once one has data the other is hidden, and a link
 * switches (clearing what the first one captured).
 */
export type CaptureChoice = 'scan' | 'manual';

export const resolveCaptureChoice = (opts: {
  /** Set by the switch links; wins over what the guest has. */
  explicit?: CaptureChoice;
  scanned: boolean;
  typed: boolean;
}): CaptureChoice | null => opts.explicit ?? (opts.scanned ? 'scan' : opts.typed ? 'manual' : null);

/** `scanAllowed` is false for the main guest in Share link mode: there is no scan, only the typed details. */
export const captureSections = (opts: { choice: CaptureChoice | null; scanAllowed: boolean }) => {
  const showScan = opts.scanAllowed && opts.choice !== 'manual';
  const showManual = !opts.scanAllowed || opts.choice !== 'scan';
  return { showScan, showManual, showOr: showScan && showManual };
};

/**
 * The explicit «manual» choice only holds while something is typed: emptying the fields drops it, so both options are
 * offered again. An explicit «scan» is kept (nothing typed is its normal state).
 */
export const choiceAfterTyping = (explicit: CaptureChoice | undefined, typed: boolean): CaptureChoice | undefined =>
  typed || explicit !== 'manual' ? explicit : undefined;

/**
 * The phone input reports the dial code even when the field is empty ("+1"), so "typed a phone" means digits beyond it.
 */
export const hasPhoneDigits = (phone: string | undefined, dialCode: string): boolean => {
  const digits = (phone ?? '').replace(/\D/g, '');
  const dial = dialCode.replace(/\D/g, '');
  return (dial && digits.startsWith(dial) ? digits.slice(dial.length) : digits).length > 0;
};
